// Knowledge base: the plain words and small rules the screens share (desktop and phone). No React here, so the
// rules have unit tests: file types, lengths, page labels, scope dots, and turning a summary into the edit form and back.
import type { FileItem, FileSummary, FileUnit, SpaceColor } from '@dpw/shared';
import { localDay, shortDay } from '../lib/format';

export type Kind = 'pdf' | 'doc' | 'xls' | 'ppt' | 'img' | 'txt';
const EXT: Record<string, Kind> = { pdf: 'pdf', doc: 'doc', docx: 'doc', xls: 'xls', xlsx: 'xls', csv: 'xls', ppt: 'ppt', pptx: 'ppt', png: 'img', jpg: 'img', jpeg: 'img', gif: 'img', webp: 'img' };
export const kindOf = (name: string): Kind => EXT[(name.split('.').pop() ?? '').toLowerCase()] ?? 'txt';
/** The short tag in the coloured square, and the type in words. */
export const KIND: Record<Kind, [string, string]> = {
  pdf: ['PDF', 'PDF document'], doc: ['DOC', 'Word document'], xls: ['XLS', 'Excel workbook'], ppt: ['PPT', 'PowerPoint deck'], img: ['IMG', 'Image'], txt: ['TXT', 'Text file'],
};

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
/** "42 pages", "3 sheets", "21 slides", "1 image"; "Unknown" when it could not be read; "" while reading. */
export function lengthOf(f: Pick<FileItem, 'pageCount' | 'unit' | 'status' | 'name'>): string {
  if (kindOf(f.name) === 'img' && f.status !== 'unreadable') return f.pageCount ? '1 image' : '';
  if (!f.pageCount || !f.unit) return f.status === 'unreadable' ? 'Unknown' : '';
  return plural(f.pageCount, f.unit);
}

/** Where a key point is: "p. 14", "sheet 2", "slide 3", "section 1". */
export function refText(unit: FileUnit | null, ref: number | null): string {
  if (!ref) return '';
  return unit === 'sheet' ? `sheet ${ref}` : unit === 'slide' ? `slide ${ref}` : unit === 'section' ? `section ${ref}` : `p. ${ref}`;
}

/** The dot for a scope: Team-wide is grey, a space has its own colour. */
export const scopeDot = (color: SpaceColor | null | undefined) => (color ? `var(--${color})` : 'var(--faint)');

/** "Today", else "27 Oct". */
export function dayText(iso: string, today = localDay(new Date().toISOString())): string {
  const d = localDay(iso);
  return d === today ? 'Today' : shortDay(d);
}

/** "27 Oct by Faisal", "today by Kareem". */
export const updatedText = (at: string, by: string | null) => `${dayText(at) === 'Today' ? 'today' : dayText(at)}${by ? ` by ${by.split(' ')[0]}` : ''}`;

/** The line under a file's name in a list, by its state. */
export function lineOf(f: Pick<FileItem, 'status' | 'unreadableReason' | 'summaryLine'>): { kind: 'ok' | 'reading' | 'locked'; text: string } {
  if (f.summaryLine) return { kind: 'ok', text: f.summaryLine };
  if (f.status === 'reading') return { kind: 'reading', text: 'Reading... the summary appears when it is ready' };
  if (f.status === 'unreadable') {
    const why = /password/i.test(f.unreadableReason ?? '') ? 'It is password protected' : /damaged/i.test(f.unreadableReason ?? '') ? 'It is damaged' : 'The assistant cannot read this type';
    return { kind: 'locked', text: `Couldn't read this file. ${why}: add a description` };
  }
  return { kind: 'ok', text: 'No summary yet' };
}

// ---------- the summary as an edit form, and back ----------

export interface SummaryForm { what: string; kp: string; dates: string; people: string; nums: string; lang: string }
const NUM = /^((?:SAR\s)?[\d.,]+%?(?:\s(?:hours?|years?|months?|days?|weeks?|minutes?|classes|roles|teams|quotes|use cases|data sets|departments|links|actions|workstreams|questions|pages|files))?)\s*(.*)$/i;

/** A date line: "Approved by the Board, 12 Mar 2026". */
export const dateLine = (d: FileSummary['dates'][number]) => (d.what && d.what !== d.date ? `${d.what}, ${d.date}` : d.date);
export const personLine = (p: FileSummary['peopleOrgs'][number]) => (p.role ? `${p.name}, ${p.role}` : p.name);
export const numberLine = (n: FileSummary['numbers'][number]) => (n.what && n.what !== n.value ? `${n.value} ${n.what}` : n.value);
/** "42 pages · English". */
export const langLine = (s: Pick<FileSummary, 'length' | 'language'>) => [s.length, s.language].filter(Boolean).join(' · ');

export function toForm(s: FileSummary, unit: FileUnit | null): SummaryForm {
  return {
    what: s.whatItIs,
    kp: s.keyPoints.map(k => (k.ref ? `${k.text} (${refText(unit, k.ref)})` : k.text)).join('\n'),
    dates: s.dates.map(dateLine).join('\n'),
    people: s.peopleOrgs.map(personLine).join('\n'),
    nums: s.numbers.map(numberLine).join('\n'),
    lang: langLine(s),
  };
}

const lines = (t: string) => t.split('\n').map(x => x.trim()).filter(Boolean);
const cut = (t: string, n: number) => t.slice(0, n);

/** Back from the form. A key point keeps its page when it ends with one in brackets: "(p. 14)", "(sheet 2)", "(slide 3)". */
export function fromForm(f: SummaryForm): FileSummary {
  const keyPoints = lines(f.kp).slice(0, 8).map(l => {
    const m = l.match(/^(.*?)\s*\(\s*(?:p\.?|page|sheet|slide|section)\s*(\d+)\s*\)$/i);
    return m && m[1] ? { text: cut(m[1].trim(), 600), ref: Number(m[2]) } : { text: cut(l, 600), ref: null };
  });
  const dates = lines(f.dates).slice(0, 12).map(l => {
    const i = l.lastIndexOf(', ');
    return i > 0 ? { what: cut(l.slice(0, i), 300), date: cut(l.slice(i + 2), 60) } : { what: cut(l, 300), date: cut(l, 60) };
  });
  const peopleOrgs = lines(f.people).slice(0, 16).map(l => {
    const i = l.indexOf(', ');
    return i > 0 ? { name: cut(l.slice(0, i), 120), role: cut(l.slice(i + 2), 200) } : { name: cut(l, 120), role: '' };
  });
  const numbers = lines(f.nums).slice(0, 12).map(l => {
    const m = l.match(NUM);
    return m && m[1] ? { value: cut(m[1], 60), what: cut(m[2] || m[1], 300) } : { value: cut(l.split(' ')[0] ?? l, 60), what: cut(l, 300) };
  });
  const [length = '', ...rest] = f.lang.split(' · ');
  return { whatItIs: cut(f.what.trim(), 1000), keyPoints, dates, peopleOrgs, numbers, length: cut(length.trim(), 60), language: cut(rest.join(' · ').trim(), 60) };
}

/** The key point a citation points at (same page), for the highlight in the file sheet; -1 when none. */
export const keyPointAt = (s: FileSummary | undefined, ref: number | null | undefined) => (s && ref ? s.keyPoints.findIndex(k => k.ref === ref) : -1);
